import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    api, DatabaseEngine, DatabaseOverview, DbQueryResult, DbRows, DbTables, formatDate, formatPrice, ManagedDatabase, Order,
} from '../../api';
import { Alert, Loading, StatusTag, useAction, useLoad } from '../../Components/ui';
import type { TabProps } from './ControlPanel';

const ENGINE_LABEL: Record<DatabaseEngine, string> = { mysql: 'MySQL 8.0', postgres: 'PostgreSQL 16' };
const PAGE_SIZE = 50;

function generatePassword(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!#%+?';
    const bytes = crypto.getRandomValues(new Uint8Array(20));
    return Array.from(bytes, b => chars[b % chars.length]).join('');
}

const cell = (v: unknown) => (v === null || v === undefined ? <span className="card-text">NULL</span> : typeof v === 'object' ? JSON.stringify(v) : String(v));

function RowsTable({ data }: { data: DbRows }) {
    if (!data.columns.length) return null;
    return (
        <div className="table-wrap" style={{ maxHeight: '28rem', overflow: 'auto' }}>
            <table className="data-table">
                <thead><tr>{data.columns.map(c => <th key={c} className="mono">{c}</th>)}</tr></thead>
                <tbody>
                    {data.rows.map((r, i) => <tr key={i}>{r.map((v, j) => <td key={j} className="mono">{cell(v)}</td>)}</tr>)}
                    {data.rows.length === 0 && <tr><td colSpan={data.columns.length} className="empty-state">No rows.</td></tr>}
                </tbody>
            </table>
        </div>
    );
}

/** Database Studio: tables, paginated rows, SQL and a .sql export (administration package §4.5–4.8). */
function Studio({ base, db, onClose }: { base: string; db: ManagedDatabase; onClose: () => void }) {
    const dbBase = `${base}/databases/${encodeURIComponent(db.name)}`;
    const { data: tables, error, loading } = useLoad(() => api.get<{ tables: DbTables }>(`${dbBase}/tables`).then(r => r.data.tables), [dbBase]);
    const [table, setTable] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState<DbRows | null>(null);
    const [sql, setSql] = useState('');
    const [result, setResult] = useState<DbQueryResult | null>(null);
    const action = useAction();

    const open = (name: string, p = 1) => void action.run(async () => {
        const res = await api.get<{ data: DbRows }>(`${dbBase}/tables/${encodeURIComponent(name)}/data`, { params: { page: p, limit: PAGE_SIZE } });
        setTable(name);
        setPage(p);
        setRows(res.data.data);
    });
    const run = (e: FormEvent) => {
        e.preventDefault();
        void action.run(async () => {
            setResult((await api.post<{ result: DbQueryResult }>(`${dbBase}/query`, { query: sql })).data.result);
        });
    };
    const download = () => void action.run(async () => {
        const res = await api.get<Blob>(`${dbBase}/export`, { responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        const a = Object.assign(document.createElement('a'), { href: url, download: `${db.name}_backup.sql` });
        a.click();
        URL.revokeObjectURL(url);
    });
    const pages = rows?.total ? Math.max(1, Math.ceil(rows.total / PAGE_SIZE)) : null;

    return (
        <div className="panel-card">
            <div className="panel-card-header">
                <h3>Database Studio · <span className="mono">{db.name}</span></h3>
                <div className="btn-row">
                    <button className="btn-outline small" onClick={download} disabled={action.busy}>Download .sql</button>
                    <button className="btn-outline small" onClick={onClose}>Close</button>
                </div>
            </div>
            {error && <Alert type="error">{error}</Alert>}
            {action.error && <Alert type="error">{action.error}</Alert>}
            {loading && !tables ? <Loading /> : tables && (
                <>
                    <p className="card-text" style={{ marginBottom: '0.75rem' }}>{tables.tables.length} tables{tables.totalSize ? ` · ${tables.totalSize}` : ''}</p>
                    <div className="table-wrap">
                        <table className="data-table">
                            <thead><tr><th>Table</th><th>Rows</th><th>Size</th><th /></tr></thead>
                            <tbody>
                                {tables.tables.map(t => (
                                    <tr key={t.name}>
                                        <td className="mono">{t.name}</td>
                                        <td>{t.rows ?? '—'}</td>
                                        <td>{t.size ?? '—'}</td>
                                        <td className="actions"><button className="btn-outline small" onClick={() => open(t.name)} disabled={action.busy}>{table === t.name ? 'Showing' : 'Browse'}</button></td>
                                    </tr>
                                ))}
                                {tables.tables.length === 0 && <tr><td colSpan={4} className="empty-state">No tables yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {table && rows && (
                <div style={{ marginTop: '1.5rem' }}>
                    <div className="panel-card-header">
                        <h4 style={{ margin: 0, color: '#fff' }} className="mono">{table}</h4>
                        <div className="btn-row" style={{ alignItems: 'center' }}>
                            <button className="btn-outline small" disabled={action.busy || page <= 1} onClick={() => open(table, page - 1)}>← Previous</button>
                            <span className="card-text">Page {page}{pages ? ` of ${pages}` : ''}</span>
                            <button className="btn-outline small" disabled={action.busy || (pages !== null ? page >= pages : rows.rows.length < PAGE_SIZE)} onClick={() => open(table, page + 1)}>Next →</button>
                        </div>
                    </div>
                    <RowsTable data={rows} />
                </div>
            )}

            <form className="fi-form" style={{ marginTop: '1.5rem' }} onSubmit={run}>
                <label>
                    SQL query
                    <textarea className="mono" rows={4} value={sql} onChange={e => setSql(e.target.value)} placeholder={`SELECT * FROM ${tables?.tables[0]?.name ?? 'users'} LIMIT 10;`} required maxLength={20000} />
                    <span className="hint">Runs directly against {db.name}. Changes can't be undone — download a .sql export first.</span>
                </label>
                <button className="btn-submit inline" disabled={action.busy}>Run query</button>
            </form>
            {result && (
                <div style={{ marginTop: '1rem' }}>
                    {(result.message || result.affectedRows !== null) && (
                        <Alert type="info">{result.message ?? ''}{result.affectedRows !== null ? ` · ${result.affectedRows} rows affected` : ''}</Alert>
                    )}
                    <RowsTable data={result} />
                </div>
            )}
        </div>
    );
}

/** Databases on a web package: the included one (WordPress) and paid extra databases (49 kr/month each). */
export default function DatabaseTab({ base, detail }: TabProps) {
    const { data, error, loading, reload } = useLoad(() => api.get<DatabaseOverview>(`${base}/databases`).then(r => r.data), [base]);
    // WordPress packages use MySQL 8.0; every other web package uses PostgreSQL 16 (decided by the server too).
    const engine: DatabaseEngine = detail.service.type === 'wordpress' ? 'mysql' : 'postgres';
    const [form, setForm] = useState<{ nameSuffix: string }>({ nameSuffix: '' });
    const [passwordFor, setPasswordFor] = useState<{ db: ManagedDatabase; password: string } | null>(null);
    const [shown, setShown] = useState<{ db: ManagedDatabase; password: string } | null>(null);
    const [studio, setStudio] = useState<ManagedDatabase | null>(null);
    const action = useAction();
    const navigate = useNavigate();
    const prefix = (detail.service.domain ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 20);

    // Selection → order → checkout/payment → CarbonEdge mounts it on this site.
    const buy = (e: FormEvent) => {
        e.preventDefault();
        void action.run(async () => {
            const res = await api.post<{ order: Order }>('/orders', {
                productSlug: data!.offer!.slug, parentServiceId: detail.service.id, database: { engine, nameSuffix: form.nameSuffix },
            });
            navigate(`/checkout/order/${res.data.order.id}`);
        });
    };
    const setPassword = (e: FormEvent) => {
        e.preventDefault();
        if (!passwordFor) return;
        void action.run(async () => {
            await api.post(`${base}/databases/${encodeURIComponent(passwordFor.db.id)}/password`, { password: passwordFor.password });
            setShown(passwordFor);
            setPasswordFor(null);
        });
    };
    const remove = (db: ManagedDatabase) => {
        if (!window.confirm(`Delete ${db.name}? All data in it is lost and the monthly fee stops.`)) return;
        void action.run(async () => {
            await api.delete(`${base}/databases/${encodeURIComponent(db.id)}`);
            if (studio?.id === db.id) setStudio(null);
            await reload();
        });
    };

    return (
        <>
            <div className="panel-card">
                <div className="panel-card-header">
                    <h3>Databases</h3>
                    {/* phpMyAdmin is for MySQL, i.e. WordPress packages. */}
                    {data?.pmaUrl && engine === 'mysql' && <a className="btn-outline small" href={data.pmaUrl} target="_blank" rel="noopener noreferrer">Open phpMyAdmin ↗</a>}
                </div>
                {error && <Alert type="error">{error}</Alert>}
                {action.error && <Alert type="error">{action.error}</Alert>}
                {shown && (
                    <div className="credentials-box" style={{ marginBottom: '1rem' }}>
                        <p>The password for {shown.db.name} was set. Copy it now — it will not be shown again.</p>
                        <div className="cred-row"><span>User</span><span className="mono">{shown.db.username}</span></div>
                        <div className="cred-row">
                            <span>Password</span><span className="mono">{shown.password}</span>
                            <button className="link-button" onClick={() => navigator.clipboard?.writeText(shown.password)}>Copy</button>
                        </div>
                        <div className="btn-row" style={{ marginTop: '0.75rem' }}><button className="btn-outline small" onClick={() => setShown(null)}>Done</button></div>
                    </div>
                )}
                {data?.pending.map(p => (
                    <Alert key={p.serviceId} type={p.status === 'failed' ? 'error' : 'info'}>
                        {p.status === 'failed'
                            ? <>An extra database could not be mounted{p.error ? `: ${p.error}` : ''}. <Link className="text-link" to={`/panel/${p.serviceId}`}>Retry setup</Link></>
                            : 'An extra database is being mounted…'}
                    </Alert>
                ))}
                {loading && !data ? <Loading /> : (
                    <div className="table-wrap">
                        <table className="data-table">
                            <thead><tr><th>Name</th><th>Type</th><th>User / host</th><th>Size</th><th>Plan</th><th /></tr></thead>
                            <tbody>
                                {data?.databases.map(db => (
                                    <tr key={db.id}>
                                        <td className="mono">{db.name}</td>
                                        <td>{ENGINE_LABEL[db.engine]}</td>
                                        <td className="mono">{db.username}<div className="hint">{db.host}:{db.port}</div></td>
                                        <td>{db.size ?? '—'}</td>
                                        <td>
                                            {db.addon
                                                ? <>{data.offer ? formatPrice(data.offer.priceOre, data.offer.billingPeriod) : 'Add-on'}{db.addon.status !== 'active' && <> <StatusTag status={db.addon.status} /></>}<div className="hint">renews {formatDate(db.addon.renewsAt)}</div></>
                                                : <span className="tag tag-muted">Included</span>}
                                        </td>
                                        <td className="actions">
                                            <button className="btn-outline small" onClick={() => setStudio(db)} disabled={action.busy}>Studio</button>
                                            <button className="btn-outline small" onClick={() => setPasswordFor({ db, password: generatePassword() })} disabled={action.busy}>Set password</button>
                                            {db.addon && <button className="btn-outline small danger" onClick={() => remove(db)} disabled={action.busy}>Delete</button>}
                                        </td>
                                    </tr>
                                ))}
                                {data?.databases.length === 0 && <tr><td colSpan={6} className="empty-state">No databases yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}
                {passwordFor && (
                    <form className="fi-form" style={{ marginTop: '1rem' }} onSubmit={setPassword}>
                        <label>
                            New password for <span className="mono">{passwordFor.db.name}</span>
                            <input type="text" className="mono" value={passwordFor.password} onChange={e => setPasswordFor(p => p && { ...p, password: e.target.value })} required minLength={12} maxLength={128} autoComplete="new-password" />
                            <span className="hint">
                                At least 12 characters. Update your site's configuration afterwards.{' '}
                                <button type="button" className="link-button" onClick={() => setPasswordFor(p => p && { ...p, password: generatePassword() })}>Generate another</button>
                            </span>
                        </label>
                        <div className="btn-row">
                            <button className="btn-submit inline" disabled={action.busy}>Set password</button>
                            <button type="button" className="btn-outline" onClick={() => setPasswordFor(null)}>Cancel</button>
                        </div>
                    </form>
                )}
                <p className="card-text" style={{ marginTop: '1rem' }}>
                    Databases are reachable from your site inside the CarbonEdge cluster.{detail.service.type === 'wordpress' ? ' WordPress is already configured to use its database.' : ''}
                </p>
            </div>

            {studio && <Studio key={studio.id} base={base} db={studio} onClose={() => setStudio(null)} />}

            {data?.offer && (
                <div className="panel-card">
                    <div className="panel-card-header">
                        <h3>Add a database</h3>
                        <span className="tag tag-category">{formatPrice(data.offer.priceOre, data.offer.billingPeriod)}</span>
                    </div>
                    <p className="card-text" style={{ marginBottom: '1rem' }}>
                        A dedicated {ENGINE_LABEL[engine]} database mounted on {detail.service.domain}, with automatic backups and Database Studio.
                        You pay at checkout; it's ready right after payment.
                    </p>
                    <form className="fi-form" onSubmit={buy}>
                        <div className="inline-fields">
                            <label>
                                Type
                                <input value={ENGINE_LABEL[engine]} readOnly disabled />
                                <span className="hint">{engine === 'mysql' ? 'WordPress packages use MySQL' : 'Web hosting packages use PostgreSQL'}</span>
                            </label>
                            <label>
                                Name
                                <input value={form.nameSuffix} onChange={e => setForm(f => ({ ...f, nameSuffix: e.target.value.trim().toLowerCase() }))} required pattern="[a-z][a-z0-9_]{0,15}" placeholder="shop" />
                                <span className="hint">Becomes <span className="mono">{prefix}_{form.nameSuffix || 'shop'}</span></span>
                            </label>
                            <button className="btn-submit inline" disabled={action.busy || detail.service.status !== 'active'}>Continue to payment →</button>
                        </div>
                    </form>
                </div>
            )}
        </>
    );
}
